import { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, Search, Upload, Download, Filter, Trash2, FileText, FolderOpen, Edit, Calendar } from 'lucide-react';
import api from '../lib/api';
import { fmtDate } from '../lib/dateUtils';
import { FilePreview } from '../components/FilePreview';
import { useAuth } from '../contexts/AuthContext';

const DOC_TYPE_LABELS = {
  giornale_cantiere: 'Giornale Cantiere',
  sal: 'SAL',
  avanzamento_lavori: 'Avanzamento Lavori',
  foto_cantiere: 'Foto Cantiere',
  verbale_riunione: 'Verbale Riunione',
  documento_tecnico: 'Documento Tecnico',
  report: 'Report',
  certificazione: 'Certificazione',
  altro: 'Altro'
};

export default function ProjectDocsPage() {
  const { isRole } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterSite, setFilterSite] = useState('');
  const [filterType, setFilterType] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showUpload, setShowUpload] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [form, setForm] = useState({
    site_id: '',
    doc_type: 'altro',
    title: '',
    description: '',
    document_date: new Date().toISOString().split('T')[0],
    reference_number: '',
    notes: ''
  });

  const load = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (filterSite && filterSite !== '__all__') params.site_id = filterSite;
      if (filterType && filterType !== '__all__') params.doc_type = filterType;

      const [docsRes, sitesRes] = await Promise.all([
        api.get('/project-documents', { params }),
        api.get('/sites').catch(() => ({ data: [] }))
      ]);
      setDocuments(docsRes.data);
      setSites(sitesRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [search, filterSite, filterType]);

  const handleCreate = async () => {
    try {
      const res = await api.post('/project-documents', form);
      setShowCreate(false);
      setForm({
        site_id: '',
        doc_type: 'altro',
        title: '',
        description: '',
        document_date: new Date().toISOString().split('T')[0],
        reference_number: '',
        notes: ''
      });
      // If no file, just reload. If user wants to upload, show upload dialog
      setShowUpload(res.data.id);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  const handleUpload = async (docId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      await api.post(`/project-documents/${docId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setShowUpload(null);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore upload');
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Eliminare questo documento?')) return;
    try {
      await api.delete(`/project-documents/${docId}`);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  const handleEdit = (doc) => {
    setSelectedDoc(doc);
    setForm({
      site_id: doc.site_id || '',
      doc_type: doc.doc_type || 'altro',
      title: doc.title || '',
      description: doc.description || '',
      document_date: doc.document_date || '',
      reference_number: doc.reference_number || '',
      notes: doc.notes || ''
    });
    setShowEdit(true);
  };

  const handleSaveEdit = async () => {
    try {
      await api.put(`/project-documents/${selectedDoc.id}`, form);
      setShowEdit(false);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  // Group documents by site for folder view
  const docsBySite = documents.reduce((acc, doc) => {
    const siteName = doc.site_name || 'Non assegnato';
    if (!acc[siteName]) acc[siteName] = [];
    acc[siteName].push(doc);
    return acc;
  }, {});

  const canManage = isRole('admin', 'aspp', 'qhse', 'capo_cantiere');

  return (
    <div className="space-y-6 animate-fade-in" data-testid="project-docs-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Archivio Documenti</h1>
          <p className="text-sm text-slate-500 mt-1">Documenti progetti e cantieri</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 sm:w-48">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Cerca documento..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="doc-search" />
          </div>
          <Select value={filterSite} onValueChange={setFilterSite}>
            <SelectTrigger className="w-44" data-testid="filter-site">
              <FolderOpen size={14} className="mr-1" />
              <SelectValue placeholder="Cantiere" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tutti i cantieri</SelectItem>
              {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-44" data-testid="filter-type">
              <Filter size={14} className="mr-1" />
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tutti i tipi</SelectItem>
              {Object.entries(DOC_TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          {canManage && (
            <Button onClick={() => setShowCreate(true)} data-testid="add-doc-btn">
              <Plus size={16} className="mr-1" /> Nuovo Documento
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-slate-900">{documents.length}</p>
            <p className="text-xs text-slate-500">Documenti Totali</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-blue-600">{Object.keys(docsBySite).length}</p>
            <p className="text-xs text-slate-500">Progetti/Cantieri</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-green-600">{documents.filter(d => d.doc_type === 'giornale_cantiere').length}</p>
            <p className="text-xs text-slate-500">Giornali Cantiere</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-amber-600">{documents.filter(d => d.doc_type === 'sal').length}</p>
            <p className="text-xs text-slate-500">SAL</p>
          </CardContent>
        </Card>
      </div>

      {/* Documents Table */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>Titolo</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Cantiere</TableHead>
                <TableHead>File</TableHead>
                {canManage && <TableHead className="w-24">Azioni</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">Caricamento...</TableCell></TableRow>
              ) : documents.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">Nessun documento trovato</TableCell></TableRow>
              ) : documents.map(doc => (
                <TableRow key={doc.id} className="hover:bg-slate-50" data-testid={`doc-row-${doc.id}`}>
                  <TableCell className="text-sm">
                    <div className="flex items-center gap-1">
                      <Calendar size={14} className="text-slate-400" />
                      {fmtDate(doc.document_date)}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="text-sm font-medium">{doc.title}</p>
                      {doc.reference_number && <p className="text-xs text-slate-500">Rif: {doc.reference_number}</p>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-200 text-xs">
                      {DOC_TYPE_LABELS[doc.doc_type] || doc.doc_type}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-sm">
                      <FolderOpen size={14} className="text-amber-500" />
                      {doc.site_name || 'Non assegnato'}
                    </div>
                  </TableCell>
                  <TableCell>
                    {doc.file_url ? (
                      <a href={`${process.env.REACT_APP_BACKEND_URL}${doc.file_url}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 text-xs flex items-center gap-1">
                        <Download size={14} /> Scarica
                      </a>
                    ) : canManage ? (
                      <Button variant="ghost" size="sm" onClick={() => setShowUpload(doc.id)} className="text-xs p-1 h-auto">
                        <Upload size={14} className="mr-1" /> Carica
                      </Button>
                    ) : <span className="text-slate-400 text-xs">-</span>}
                  </TableCell>
                  {canManage && (
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(doc)} className="p-1 h-auto">
                          <Edit size={14} />
                        </Button>
                        {!doc.file_url && (
                          <Button variant="ghost" size="sm" onClick={() => setShowUpload(doc.id)} className="p-1 h-auto text-blue-600">
                            <Upload size={14} />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(doc.id)} className="p-1 h-auto text-red-600 hover:text-red-800">
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Document Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuovo Documento</DialogTitle>
            <DialogDescription>Aggiungi un documento all'archivio progetti</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5">
              <Label>Titolo *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Es: Giornale cantiere 15/03" data-testid="doc-title" />
            </div>
            <div className="space-y-1.5">
              <Label>Tipo Documento *</Label>
              <Select value={form.doc_type} onValueChange={(v) => setForm({ ...form, doc_type: v })}>
                <SelectTrigger data-testid="doc-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(DOC_TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Data Documento *</Label>
              <Input type="date" value={form.document_date} onChange={(e) => setForm({ ...form, document_date: e.target.value })} data-testid="doc-date" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Cantiere/Progetto</Label>
              <Select value={form.site_id || 'none'} onValueChange={(v) => setForm({ ...form, site_id: v === 'none' ? '' : v })}>
                <SelectTrigger data-testid="doc-site"><SelectValue placeholder="Seleziona cantiere..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuno (documento generale)</SelectItem>
                  {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Riferimento</Label>
              <Input value={form.reference_number} onChange={(e) => setForm({ ...form, reference_number: e.target.value })} placeholder="N. protocollo, rif..." />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Descrizione</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Breve descrizione..." />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Note</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Annulla</Button>
            <Button onClick={handleCreate} disabled={!form.title || !form.document_date} data-testid="doc-save-btn">Crea e Carica File</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Document Dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Modifica Documento</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5"><Label>Titolo *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <Select value={form.doc_type} onValueChange={(v) => setForm({ ...form, doc_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(DOC_TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Data</Label><Input type="date" value={form.document_date} onChange={(e) => setForm({ ...form, document_date: e.target.value })} /></div>
            <div className="col-span-2 space-y-1.5">
              <Label>Cantiere</Label>
              <Select value={form.site_id || 'none'} onValueChange={(v) => setForm({ ...form, site_id: v === 'none' ? '' : v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuno</SelectItem>
                  {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1.5"><Label>Descrizione</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEdit(false)}>Annulla</Button>
            <Button onClick={handleSaveEdit}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload File Dialog */}
      <Dialog open={!!showUpload} onOpenChange={(o) => { if (!o) { setShowUpload(null); setPendingFile(null); } }}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Carica File</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <Input
              type="file"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.xls,.xlsx,.zip"
              onChange={(e) => setPendingFile(e.target.files?.[0] || null)}
              data-testid="upload-file-input"
            />
            <p className="text-xs text-slate-500">Formati: PDF, Word, Excel, immagini, ZIP</p>
            <FilePreview file={pendingFile} />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setShowUpload(null); setPendingFile(null); }}>Annulla</Button>
            <Button onClick={() => { handleUpload(showUpload, pendingFile); setPendingFile(null); }} disabled={!pendingFile} data-testid="upload-confirm-btn">Carica</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
